import { Slot } from "@radix-ui/react-slot"
import { type VariantProps, cva } from "class-variance-authority"
import { LoaderCircle } from "lucide-react"
import * as React from "react"
import { useEffect, useState } from "react"
import { cn } from "~/utils/style"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20",
        outline:
          "border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        warning: "bg-orange-500 text-orange-50 hover:bg-orange-500/90",
        success: "bg-green-500 text-green-50 hover:bg-green-500/90",
      },
      size: {
        default: "h-9 rounded-lg px-4 py-2",
        sm: "h-8 rounded-lg px-3 text-xs",
        lg: "h-10 rounded-lg px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  },
)
Button.displayName = "Button"

export interface LoadingButtonProps extends ButtonProps {
  loading: boolean
}

const LoadingButton = React.forwardRef<HTMLButtonElement, LoadingButtonProps>(
  ({ loading, children, disabled, ...props }, ref) => {
    const [child, setChild] = useState(children)

    useEffect(() => {
      let timer: NodeJS.Timer
      if (loading) {
        timer = setTimeout(() => {
          setChild(<LoaderCircle className="animate-spin" />)
        }, 200)
      } else {
        setChild(children)
      }

      return () => clearTimeout(timer)
    }, [loading, children])

    return (
      <Button
        {...props}
        ref={ref}
        children={child}
        disabled={disabled ?? loading}
      />
    )
  },
)
LoadingButton.displayName = "LoadingButton"

export { Button, LoadingButton, buttonVariants }
